import { useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { RefShape } from "../../shapes";

const PinnedElement = ({ pinTo, children, style, inheritWidth = true }) => {
  const el = useMemo(() => {
    if (typeof document === "undefined") return;
    return document.createElement("div");
  }, []);

  const fixPosition = useCallback(() => {
    if (typeof document === "undefined") return;

    if (pinTo?.current) {
      const pinToRect = pinTo.current.getBoundingClientRect();
      let top = pinToRect.bottom + document.documentElement.scrollTop;

      // If it goes off screen, move it up by the difference + 20px bottom padding, and bring to top
      const diff =
        window.innerHeight -
        (pinToRect.top + pinToRect.height + el.scrollHeight);
      if (diff < 0) {
        top += diff - 20;
        el.style.setProperty("z-index", 5000);
      }

      el.style.setProperty("top", `${top}px`);
      el.style.setProperty("left", `${pinToRect.left}px`);
      if (inheritWidth) el.style.setProperty("width", `${pinToRect.width}px`);
    }
  }, [pinTo, el, inheritWidth]);

  useEffect(() => {
    if (typeof window === "undefined") return; // SSR
    window.addEventListener("scroll", fixPosition, { passive: true });
    window.addEventListener("resize", fixPosition, { passive: true });

    return () => {
      window.removeEventListener("scroll", fixPosition);
      window.removeEventListener("resize", fixPosition);
    };
  });

  useEffect(() => {
    if (typeof window === "undefined") return; // SSR
    document.body.appendChild(el);

    return () => {
      document.body.removeChild(el);
    };
  }, [el]);

  useEffect(fixPosition, [children]);

  if (!el) return null;

  el.style.setProperty("position", "absolute");
  if (style)
    Object.entries(style).forEach(([k, v]) => el.style.setProperty(k, v));

  return createPortal(children, el);
};

PinnedElement.propTypes = {
  pinTo: RefShape,
  children: PropTypes.node.isRequired,
  style: PropTypes.object,
  inheritWidth: PropTypes.bool,
};

export default PinnedElement;
